import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, toApiError } from '../api/errors';
import { isAbort } from '../api/http';

/**
 * 🔴 THE HOOK THAT ENCODES THE HOUSE RULE: stale and current must not look the same to a reader.
 *
 * The usual async hook has three states — loading, data, error — and on a failed REFETCH it either
 * throws the old data away or keeps rendering it as though it were fresh. The second is the one that
 * bites: the screen is indistinguishable from a successful load, so a reader acts on data that may
 * be minutes or hours old and has no way to tell.
 *
 * ⇒ Here, data and error coexist. `isStale` is true exactly when we are still showing something that
 * we have since failed to refresh, and the caller is expected to render that state VISIBLY —
 * see `StaleBanner`. `loadedAt` gives the reader the age, which is the fact they actually need.
 */
export interface Resource<T> {
  data: T | null;
  error: ApiError | null;
  /** No data yet and a request is in flight. The only state that should show a skeleton. */
  isLoading: boolean;
  /** Data on screen and a request in flight. Shows a subtle indicator, never a skeleton. */
  isRefreshing: boolean;
  /** Data on screen that we have FAILED to refresh. Must be shown as such. */
  isStale: boolean;
  /** When `data` was actually fetched — not when it was rendered. */
  loadedAt: Date | null;
  reload: () => void;
}

interface State<T> {
  data: T | null;
  error: ApiError | null;
  inFlight: boolean;
  loadedAt: Date | null;
}

export function useResource<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  options: { enabled?: boolean } = {},
): Resource<T> {
  const enabled = options.enabled ?? true;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [state, setState] = useState<State<T>>({
    data: null,
    error: null,
    inFlight: enabled,
    loadedAt: null,
  });
  const [reloadToken, setReloadToken] = useState(0);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, error: null, inFlight: false, loadedAt: null });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setState((previous) => ({ ...previous, inFlight: true }));

    fetcherRef
      .current(controller.signal)
      .then((data) => {
        if (cancelled) return;
        // A success clears the error — this is the ONLY place `isStale` goes back to false.
        setState({ data, error: null, inFlight: false, loadedAt: new Date() });
      })
      .catch((err: unknown) => {
        if (cancelled || isAbort(err)) return;
        setState((previous) => ({
          // Keep the old data so the reader is not blanked mid-task, but the error rides alongside
          // it — the view is required to say so rather than pretending the render is current.
          data: previous.data,
          error: toApiError(err, 'unknown'),
          inFlight: false,
          loadedAt: previous.loadedAt,
        }));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reloadToken, ...deps]);

  const hasData = state.data !== null;
  return {
    data: state.data,
    error: state.error,
    isLoading: state.inFlight && !hasData,
    isRefreshing: state.inFlight && hasData,
    isStale: hasData && state.error !== null,
    loadedAt: state.loadedAt,
    reload,
  };
}
